
if (!customElements.get('cart-quantity-selector')) {
    class CartQuantitySelector extends HTMLElement {
        constructor() {
            super();
            this.variantId = this.getAttribute('data-variant-id');
            this.minusBtn = this.querySelector('.qty-btn-minus');
            this.plusBtn = this.querySelector('.qty-btn-plus');
            this.input = this.querySelector('.qty-display-input');
            this.cartTimeout = null;
            this.errorDiv = this.querySelector('.qty-errmsg')

            this.localQty = this.input ? (parseInt(this.input.value) || 0) : 0;
        }

        connectedCallback() {
            if (!this.variantId) {
                this.toggleControls(true);
                return;
            }
            this.minusBtn?.addEventListener('click', () => this.handleMinus());
            this.plusBtn?.addEventListener('click', () => this.handlePlus());
            this.input?.addEventListener('input', () => this.handleInput());
            document.addEventListener('cart:updated', (event) => {
                if (event.detail && event.detail.cart) {
                    this.syncState(event.detail.cart);
                }
            });
        }

        showError(message) {
            if (!this.errorDiv) return;

            if (this.errorClearTimeout) clearTimeout(this.errorClearTimeout);

            this.errorDiv.textContent = message;
            this.errorDiv.style.display = 'block';

            this.errorClearTimeout = setTimeout(() => {
                this.hideError();
            }, 30000);
        }

        hideError() {
            if (!this.errorDiv) return;
            this.errorDiv.textContent = '';
            this.errorDiv.style.display = 'none';
            if (this.errorClearTimeout) clearTimeout(this.errorClearTimeout);
        }

        handleMinus() {
            if (this.localQty <= 0) return;
            this.localQty--;
            if (this.input) this.input.value = this.localQty;

            this.queueCartUpdate();
        }

        handlePlus() {
            this.localQty++;
            if (this.input) this.input.value = this.localQty;

            this.queueCartUpdate();
        }

        handleInput() {
            let value = parseInt(this.input.value);

            if (isNaN(value) || value < 0) {
                value = 0;
            }

            this.localQty = value;
            this.queueCartUpdate();
        }

        queueCartUpdate() {
            if (this.cartTimeout) {
                clearTimeout(this.cartTimeout);
            }

            this.cartTimeout = setTimeout(async () => {
                this.toggleControls(true);

                const finalTargetQty = this.localQty;
                const currentCartState = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then(r => r.json()).catch(() => null);

                const isItemInCart = currentCartState?.items?.some(item => item.id.toString() === this.variantId.toString());

                try {
                    let response;

                    if (finalTargetQty > 0 && !isItemInCart) {
                        response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                items: [{
                                    id: this.variantId,
                                    quantity: finalTargetQty
                                }]
                            })
                        });
                    } else {
                        response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ id: this.variantId, quantity: finalTargetQty })
                        });
                    }

                    if (response.status === 429) {
                        console.warn('API busy, retrying...');
                        this.toggleControls(false);
                        this.queueCartUpdate();
                        return;
                    }

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => null);
                        const errorMessage = errorData?.description || errorData?.message || 'Failed to update line items';
                        throw new Error(errorMessage);
                    }
                    this.hideError();

                    const freshCartRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
                    const cartData = await freshCartRes.json();

                    const cartEvent = new CustomEvent('cart:updated', {
                        bubbles: true,
                        composed: true,
                        detail: { cart: cartData }
                    });
                    document.dispatchEvent(cartEvent);

                } catch (error) {
                    console.error('debounce error:', error);
                    this.showError(error.message);
                    if (currentCartState) {
                        this.syncState(currentCartState);
                    }
                } finally {
                    this.toggleControls(false);
                }
            }, 700);
        }

        toggleControls(disabled) {
            if (this.minusBtn) this.minusBtn.disabled = disabled;
            if (this.plusBtn) this.plusBtn.disabled = disabled;
            if (this.input) this.input.disabled = disabled;
        }

        syncState(cartData) {
            if (this.cartTimeout) return;
            if (!cartData || !cartData.items) return;

            let match = cartData.items.find(item => item.id.toString() === this.variantId.toString());
            let newQty = match ? match.quantity : 0;

            this.localQty = newQty;
            if (this.input) this.input.value = newQty;

            this.toggleControls(false);
        }
    }
    customElements.define('cart-quantity-selector', CartQuantitySelector);
    document.addEventListener('cart:updated', async (event) => {
        const cartData = event.detail?.cart;
        if (!cartData) return;
        let deferredUpdate;

        try {
            const { CartLinesUpdateEvent } = await import('@shopify/events');
            deferredUpdate = CartLinesUpdateEvent.createPromise();
            const cartUpdateEvent = new CartLinesUpdateEvent({
                action: 'add',
                context: 'custom',
                lines: [],
                promise: deferredUpdate.promise
            });

            document.dispatchEvent(cartUpdateEvent);

            const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
                .map(component => component.dataset.sectionId)
                .filter(Boolean);
            let sections = {};

            if (sectionIds.length) {
                const root = window.Shopify?.routes?.root || '/';
                const separator = root.includes('?') ? '&' : '?';
                const sectionResponse = await fetch(`${root}${separator}sections=${encodeURIComponent([...new Set(sectionIds)].join(','))}`);
                if (!sectionResponse.ok) throw new Error('Unable to refresh basket sections.');
                sections = await sectionResponse.json();
            }

            deferredUpdate.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData),
                detail: {
                    sections,
                    items: cartData.items,
                    itemCount: cartData.items?.length || 0,
                    source: 'bpt-quick-order',
                    didError: false
                }
            });
        } catch (error) {
            deferredUpdate?.reject(error);
            console.error('[BPT cart refresh]', error);
        }
    });

}


if (!customElements.get('related-subtotal')) {
    class RelatedSubtotal extends HTMLElement {
        constructor() {
            super();
            this.variantId = this.getAttribute('data-variant-id');
            this.span = this.querySelector('span');
            this.handleCartUpdate = this.handleCartUpdate.bind(this)
        }

        connectedCallback() {
            document.addEventListener('cart:updated', this.handleCartUpdate);
            document.addEventListener('change', this.handleCartUpdate);
        }

        disconnectedCallback() {
            document.removeEventListener('cart:updated', this.handleCartUpdate);
            document.removeEventListener('change', this.handleCartUpdate);
        }
        updatePriceFromCart(items) {
            const matchingItem = items.find(item => item.variant_id.toString() === this.variantId.toString());

            if (matchingItem && this.span) {
                this.span.textContent = this.formatGBP(matchingItem.final_line_price);
            } else if (!matchingItem && this.span) {
                this.span.textContent = "";
            }
        }
        formatGBP(pennies) {
            const pounds = (pennies / 100).toFixed(2);
            return `£${pounds}`;
        }
        handleCartUpdate(event) {
            const cartData = event.detail?.cart;

            if (cartData && cartData.items) {
                this.updatePriceFromCart(cartData.items);
            } else {
                fetch(window.Shopify.routes.root + 'cart.js')
                    .then(response => response.json())
                    .then(cart => this.updatePriceFromCart(cart.items))
                    .catch(error => console.error('Error fetching cart:', error));
            }
        }

    }
    customElements.define('related-subtotal', RelatedSubtotal);
}
